import React, {
  Children,
  MouseEvent,
  ReactElement,
  ReactNode,
  cloneElement,
  forwardRef,
  isValidElement,
  useState,
} from "react";

import { Link, LinkProps, Menu, MenuProps } from "@mui/material";

const NAME = "LinkMenu";

export interface LinkMenuProps extends LinkProps {
  menu: ReactNode;
  menuProps?: Omit<MenuProps, "children" | "open">;
}

export const LinkMenu = forwardRef<HTMLAnchorElement, LinkMenuProps>(
  ({ onClick, menu, menuProps, ...linkProps }, linkRef) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
      e.stopPropagation();
      setAnchorEl(e.currentTarget);
      onClick?.(e);
    };
    const handleClose = () => {
      setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    return (
      <>
        <Link onClick={handleClick} ref={linkRef} {...linkProps} />
        <Menu
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          onClose={handleClose}
          open={open}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          {...menuProps}
        >
          {(() => {
            const processChildren = (children: ReactNode): ReactNode => {
              return Children.map(children, (child) => {
                if (!isValidElement(child)) return child;

                // Check if child is a Fragment
                if (
                  child.type === React.Fragment ||
                  child.type.toString() === "Symbol(react.fragment)"
                ) {
                  // Recursively process fragment children
                  return cloneElement(child as ReactElement, {
                    children: processChildren(child.props.children),
                  });
                }

                // For regular elements, add onClick handler
                return cloneElement(child as ReactElement, {
                  onClick: (e: MouseEvent<HTMLElement>) => {
                    handleClose();
                    if (child.props.onClick) child.props.onClick(e);
                  },
                });
              });
            };

            return processChildren(menu);
          })()}
        </Menu>
      </>
    );
  },
);
LinkMenu.displayName = NAME;
